import React, {InputHTMLAttributes} from 'react';
import cn from 'classnames';
import {isMobile} from 'react-device-detect';
import {ErrorType} from '../../../utils/common';
import {useScreenResize} from '../../../utils/hooks';
import {device} from '../../../styled/device';
import Tooltip from '../../Tooltip';
import styles from './Input.module.sass';
import {FieldQuestionMarkButton, TooltipTrigger} from './styled';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  invalid?: boolean;
  error?: ErrorType;
  inputMode?:
    | 'text'
    | 'none'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
  tooltipContentKey?: string;
};

const defaultProps: InputProps = {
  onChange: undefined,
  label: '',
  invalid: undefined,
  error: '',
  inputMode: undefined,
  autoComplete: 'on',
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      onChange,
      className,
      label,
      invalid,
      defaultValue,
      disabled,
      name,
      type,
      max,
      error,
      autoComplete,
      inputMode,
      children,
      tooltipContentKey,
      ...props
    },
    ref,
  ) => {
    const {isMatch} = useScreenResize(device.tablet);
    const isMobileMode = isMatch || isMobile;
    const position = isMobileMode ? 'top center' : 'top right';

    return (
      <div
        className={cn(styles.inputWrapper, className, {
          [styles.disabled]: disabled,
        })}
      >
        <label htmlFor={name} className={styles.label}>
          {label}
          {tooltipContentKey && (
            <Tooltip position={position} label={tooltipContentKey}>
              <TooltipTrigger>
                <FieldQuestionMarkButton />
              </TooltipTrigger>
            </Tooltip>
          )}
        </label>
        <div className={styles.relativeWrapper}>
          {children || (
            <input
              className={cn('input__input', styles.input, {
                [styles.invalid]: invalid || Boolean(error),
              })}
              ref={ref}
              type={type}
              data-testid="input"
              value={value}
              onChange={onChange}
              disabled={disabled}
              defaultValue={defaultValue}
              aria-label={name}
              name={name}
              max={max}
              autoComplete={autoComplete}
              inputMode={inputMode}
              {...props}
            />
          )}
        </div>

        {error && (
          <div
            className={cn([styles.errorMessage, 'input_error-message'])}
            data-testid={`${name}-error`}
          >
            {error}
          </div>
        )}
      </div>
    );
  },
);

Input.defaultProps = defaultProps;
export {Input};
